import { useAppToast } from "@/hooks/useAppToast";
import { useOrderSession } from "@/hooks/useOrderSession";
import { useWhatsAppCheckout } from "@/hooks/useWhatsApp";
import { useLazyGetFoodPaymentMethodsQuery } from "@/store/services/booking";
import {
  useFoodCheckoutMutation,
  useLazyGetBookingOrderDetailQuery,
} from "@/store/services/foodbooking";
import {
  useLazyGetUserGlobalTicketListQuery,
  useLazyGetUserPurchaseHistoryQuery,
} from "@/store/services/user";
import {
  AbsoluteCenter,
  Alert,
  AlertIcon,
  AlertProps,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  CardProps,
  CloseButton,
  Divider,
  Flex,
  FlexProps,
  Grid,
  GridItem,
  GridItemProps,
  Heading,
  HeadingProps,
  Input,
  InputProps,
  StackProps,
  Text,
  TextProps,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { GridProps } from "@chakra-ui/system";
import {
  AmountSummary,
  BaseModal,
  BaseModalProps,
  MovieCardDetailAction,
} from "@picovico/ui";
import { catchErrorHandler, getLocalStorageData } from "@picovico/utils";
import { format } from "date-fns";
import { debounce } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { getPageRoutes } from "routes/pageRoutes";
import { useAppSelector } from "store";
import { UserPurchaseHistoryDTO } from "types/api";
import { WebConfig } from "types/config";
import { WHATSAPP_CHECK_NUMBER } from "utils/whatsapp";
import BookingSkeleton from "../Booking/BookingSkeleton";
import PaymentSelector from "../PaymentSelector";
import { WhatsAppOptIn, WhatsAppOptInStyleProps } from "../WhatsApp";

export interface FoodCheckoutProps {
  topContainerProps?: BoxProps;
  gridContainerProps?: GridProps;
  gridItemProps?: GridItemProps;
  goBackButtonProps?: ButtonProps;
  foodOrderContainerProps?: BoxProps;
  foodVstackProps?: StackProps;
  boxHeaderProps?: HeadingProps;
  initialTicketAlertProps?: AlertProps;
  initialTicketAlertTextProps?: TextProps;
  ticketCodeSelectionBoxWrapperProps?: FlexProps;
  ticketCodeSelectionBoxProps?: FlexProps;
  inputFieldProps?: InputProps;
  manualTicketStatusBoxProps?: AlertProps;
  manualTicketStatusTextProps?: TextProps;
  selectTicketButtonProps?: ButtonProps;
  selectedTicketBoxProps?: AlertProps;
  selectedTicketTextProps?: TextProps;
  closeButtonProps?: ButtonProps;
  orderItemListBoxWrapperProps?: StackProps;
  orderItemListBoxProps?: FlexProps;
  orderItemLabelProps?: TextProps;
  orderItemQuantityProps?: TextProps;
  orderItemAmountProps?: TextProps;
  proceedToPayButton?: ButtonProps;
  // Ticket selection modal
  baseModalProps?: BaseModalProps;
  selectionModalGridContainerProps?: GridProps;
  selectionModalGridItemProps?: GridItemProps;
  movieCardDetailActionProps?: MovieCardDetailAction & CardProps;
  movieCardDetailTextBoxProps?: TextProps;
  // OR divider between manual input and select-from-list
  orDividerTextBg?: string;
  orDividerTextProps?: TextProps;
  // Section dividers between Ticket / Order Items / Payment
  sectionDividerProps?: BoxProps;
  // Confirm / cancel action text in manual ticket validation row
  confirmActionTextProps?: TextProps;
  // Full-page error states (ticket not found, API error)
  errorIconBoxProps?: BoxProps;
  errorHeadingProps?: HeadingProps;
  errorMessageProps?: TextProps;
  errorButtonProps?: ButtonProps;
  // Full-page success state (order already placed)
  successIconBoxProps?: BoxProps;
  successHeadingProps?: HeadingProps;
  successMessageProps?: TextProps;
  successButtonProps?: ButtonProps;
  /** Styling for the "Send my order on WhatsApp" box (whatsapp-messaging 04 §3). */
  whatsappOptInProps?: WhatsAppOptInStyleProps;
}

const TicketSelectionModal = ({
  isOpen,
  onClose,
  purchaseHistory,
  onSelect,
  selectValue,
  baseModalProps,
  selectionModalGridContainerProps,
  selectionModalGridItemProps,
  movieCardDetailActionProps,
  movieCardDetailTextBoxProps,
}: {
  isOpen: boolean;
  onClose: () => void;
  purchaseHistory: any;
  onSelect: (code: string) => void;
  selectValue: string;
  baseModalProps?: BaseModalProps;
  selectionModalGridContainerProps?: GridProps;
  selectionModalGridItemProps?: GridItemProps;
  movieCardDetailActionProps?: MovieCardDetailAction & CardProps;
  movieCardDetailTextBoxProps?: TextProps;
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Ticket"
      size={{ base: "full", md: "5xl" }}
      scrollBehavior="inside"
      {...baseModalProps}
    >
      <Grid
        templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)",
        }}
        gap={{ base: "1rem", md: "1.5rem" }}
        p={{ base: "0.25rem", md: "1rem" }}
        {...selectionModalGridContainerProps}
      >
        {purchaseHistory?.data?.records?.map((history: any) => (
          <GridItem
            key={history.ticket_code}
            cursor="pointer"
            onClick={() => {
              onSelect(history.ticket_code);
              onClose();
            }}
            transition="all 0.2s"
            _hover={{
              transform: "translateY(-2px)",
              shadow: "md",
            }}
            {...selectionModalGridItemProps}
          >
            <MovieCardDetailAction
              image={history.poster || ""}
              title={history.movie}
              detail={
                <VStack
                  align="start"
                  spacing={1}
                  color="gray.600"
                  {...movieCardDetailTextBoxProps}
                >
                  <Text fontSize="sm" fontWeight="bold" color="blue.600">
                    TC: {history.ticket_code}
                  </Text>
                  <Text fontSize="xs">Seats: {history.seats}</Text>
                  <Text fontSize="xs">
                    Time:{" "}
                    {history.show_date_time &&
                      format(
                        new Date(history.show_date_time),
                        "MMM dd, hh:mm a",
                      )}
                  </Text>
                </VStack>
              }
              {...movieCardDetailActionProps}
              sx={{
                border:
                  selectValue === history.ticket_code
                    ? "3px solid #2b6cb0"
                    : "3px solid transparent",
                ...movieCardDetailActionProps?.sx,
              }}
            />
          </GridItem>
        ))}
        {purchaseHistory?.data?.records?.length === 0 && (
          <GridItem colSpan={2} textAlign="center" py="3rem">
            <Text color="gray.500">No available tickets found.</Text>
          </GridItem>
        )}
      </Grid>
    </BaseModal>
  );
};

function FoodCheckout({
  topContainerProps,
  gridContainerProps,
  gridItemProps,
  goBackButtonProps,
  foodOrderContainerProps,
  foodVstackProps,
  boxHeaderProps,
  initialTicketAlertProps,
  initialTicketAlertTextProps,
  ticketCodeSelectionBoxWrapperProps,
  ticketCodeSelectionBoxProps,
  inputFieldProps,
  manualTicketStatusBoxProps,
  manualTicketStatusTextProps,
  selectTicketButtonProps,
  selectedTicketBoxProps,
  selectedTicketTextProps,
  closeButtonProps,
  orderItemListBoxWrapperProps,
  orderItemListBoxProps,
  orderItemLabelProps,
  orderItemQuantityProps,
  orderItemAmountProps,
  proceedToPayButton,
  orDividerTextBg,
  orDividerTextProps,
  sectionDividerProps,
  confirmActionTextProps,
  errorIconBoxProps,
  errorHeadingProps,
  errorMessageProps,
  errorButtonProps,
  successIconBoxProps,
  successHeadingProps,
  successMessageProps,
  successButtonProps,
  whatsappOptInProps,
  ...props
}: FoodCheckoutProps) {
  const router = useRouter();
  const config: WebConfig = useAppSelector((state) => state.config);
  const { user } = useAppSelector((state) => state.user);
  const { success, error: errorToast } = useAppToast();
  const { clearOrder } = useOrderSession();

  const [paymentMethod, setPaymentMethod] = useState("");
  const [manualTicketCode, setManualTicketCode] = useState("");
  const [ticketStatus, setTicketStatus] = useState<"valid" | "invalid" | "">(
    "",
  );
  const [selectedTicket, setSelectedTicket] =
    useState<UserPurchaseHistoryDTO>();
  // "Send my order on WhatsApp" (whatsapp.tickets and /whatsapp/me/ food; absent means off).
  const whatsapp = useWhatsAppCheckout("food");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const orderIdx = (router.query.orderIdx ||
    router.query.foodOrderIdx) as string;
  const initialTicketCode = router.query.ticket_code as string;
  const cinemaHall = getLocalStorageData("cinema_hall") || "";

  const [
    getBookingOrderDetail,
    { data: orderData, isLoading: isOrderLoading, error, isError },
  ] = useLazyGetBookingOrderDetailQuery({});

  const [
    getPurchaseHistory,
    { data: purchaseHistory, isLoading: isHistoryLoading },
  ] = useLazyGetUserPurchaseHistoryQuery();

  const [checkoutFood, { isLoading: isCheckoutLoading }] =
    useFoodCheckoutMutation();

  const [getFoodPaymentMethods, { data: foodPaymentMethodData }] =
    useLazyGetFoodPaymentMethodsQuery();
  const [checkGlobalTicket, { data: checkGlobalTicketData }] =
    useLazyGetUserGlobalTicketListQuery({});

  useEffect(() => {
    const query = initialTicketCode
      ? {
          ticket_code: initialTicketCode,
        }
      : {};
    getPurchaseHistory({
      search: "current",
      page_size: 100,
      page: 1,
      ...query,
    });
  }, [initialTicketCode, getPurchaseHistory]);

  useEffect(() => {
    getFoodPaymentMethods({ cinema_hall: cinemaHall });
  }, [cinemaHall, getFoodPaymentMethods]);

  useEffect(() => {
    if (orderIdx) {
      getBookingOrderDetail({ order_idx: orderIdx });
    }
  }, [orderIdx, getBookingOrderDetail]);

  const foodBreakdown = useMemo(() => {
    if (!orderData?.data) return [];

    const foodItems: {
      amount: number;
      quantity: number;
      title: string;
      showAmountUnitPrice: boolean;
    }[] = [];

    orderData.data.menu_items?.forEach((item) => {
      foodItems.push({
        title: item.title,
        amount: item.amount / item.quantity,
        quantity: item.quantity,
        showAmountUnitPrice: true,
      });
    });

    orderData.data.combo_packages?.forEach((item) => {
      foodItems.push({
        title: item.title,
        amount: item.amount / item.quantity,
        quantity: item.quantity,
        showAmountUnitPrice: true,
      });
    });

    return foodItems;
  }, [orderData]);

  const total = useMemo(() => {
    return orderData?.data?.order_total_amount || 0;
  }, [orderData]);

  const initialTicketDetail = useMemo(() => {
    if (initialTicketCode) {
      return purchaseHistory?.data?.records?.find(
        (item) => item.ticket_code === initialTicketCode,
      );
    }
    return null;
  }, [initialTicketCode, purchaseHistory]);

  const handleProceedToPay = () => {
    if (!(selectedTicket?.ticket_code || initialTicketCode) || !paymentMethod) {
      return;
    }
    // The WhatsApp number is checked before anything is charged; its error shows under the field.
    const whatsappFields = whatsapp.prepare();
    if (!whatsappFields) {
      errorToast(WHATSAPP_CHECK_NUMBER);
      return;
    }
    checkoutFood({
      food_order: orderIdx,
      payment_method: paymentMethod,
      ticket_code: initialTicketCode || selectedTicket?.ticket_code || "",
      cinema_hall: cinemaHall,
      // `whatsapp_to` (+ `whatsapp_offers: true` only when ticked); nothing when not asked for.
      ...whatsappFields,
    })
      .unwrap()
      .then((res) => {
        if (res.data?.payment_url) {
          // The success page says where the order went; the gateway leaves the site in between.
          whatsapp.remember(whatsappFields, [res.data.pidx, orderIdx]);
          window.location.href = res.data.payment_url;
        } else {
          success("Order placed successfully");
          router.push(getPageRoutes("profile", {}));
        }
        clearOrder();
      })
      .catch((err) => {
        // 400 on `whatsapp_to`: shown under the number.
        if (whatsapp.showServerError(err)) {
          errorToast(WHATSAPP_CHECK_NUMBER);
          return;
        }
        catchErrorHandler(err, errorToast);
      });
  };

  const handleTicketCheck = useMemo(
    () =>
      debounce((ticketCode: string) => {
        checkGlobalTicket({
          page_size: 100,
          page: 1,
          ticket_code: ticketCode,
        })
          .unwrap()
          .then((res) => {
            if (res?.data && res?.data?.length > 0) {
              setTicketStatus("valid");
            } else {
              setTicketStatus("invalid");
            }
          })
          .catch(() => {
            setTicketStatus("invalid");
          });
      }, 700),
    [checkGlobalTicket],
  );

  const offlineTicketDetail = checkGlobalTicketData?.data[0];

  if (isOrderLoading) {
    return <BookingSkeleton />;
  }

  if (initialTicketCode && purchaseHistory?.data?.records?.length === 0) {
    return (
      <VStack h="80vh" justify="center" spacing="1.5rem" textAlign="center">
        <Box color="red.500" fontSize="5xl" {...errorIconBoxProps}>
          <FaExclamationCircle />
        </Box>
        <VStack spacing="0.5rem">
          <Heading size="lg" {...errorHeadingProps}>
            Oops!
          </Heading>
          <Text color="gray.600" fontSize="md" {...errorMessageProps}>
            Ticket not found
          </Text>
        </VStack>
        <Button
          colorScheme="blue"
          size="lg"
          onClick={() => router.push("/")}
          {...errorButtonProps}
        >
          Go Back Home
        </Button>
      </VStack>
    );
  }

  if (isError) {
    const errorData = error as any;
    const errorMessage =
      errorData?.error?.message?.[0] || "Something went wrong";
    return (
      <VStack h="80vh" justify="center" spacing="1.5rem" textAlign="center">
        <Box color="red.500" fontSize="5xl" {...errorIconBoxProps}>
          <FaExclamationCircle />
        </Box>
        <VStack spacing="0.5rem">
          <Heading size="lg" {...errorHeadingProps}>
            Oops!
          </Heading>
          <Text color="gray.600" fontSize="md" {...errorMessageProps}>
            {errorMessage}
          </Text>
        </VStack>
        <Button
          colorScheme="blue"
          size="lg"
          onClick={() => router.push("/")}
          {...errorButtonProps}
        >
          Go Back Home
        </Button>
      </VStack>
    );
  }

  if (orderData?.data.status === "SUCCESS") {
    return (
      <VStack h="80vh" justify="center" spacing="1.5rem" textAlign="center">
        <Box color="green.500" fontSize="5xl" {...successIconBoxProps}>
          <FaCheckCircle />
        </Box>
        <VStack spacing="0.5rem">
          <Heading size="lg" {...successHeadingProps}>
            Order Already Placed!
          </Heading>
          <Text color="gray.600" fontSize="md" {...successMessageProps}>
            Your food order has been successfully processed.
          </Text>
        </VStack>
        <Button
          colorScheme="blue"
          size="lg"
          onClick={() => router.push(getPageRoutes("profile"))}
          {...successButtonProps}
        >
          View My Bookings
        </Button>
      </VStack>
    );
  }

  return (
    <Box py="2rem" {...topContainerProps}>
      <Grid
        templateColumns={{
          base: "repeat(1, 1fr)",
          md: "repeat(12, 1fr)",
        }}
        gap="2rem"
        {...gridContainerProps}
      >
        <GridItem colSpan={{ base: 1, md: 12 }} {...gridItemProps}>
          <Button
            leftIcon={<FaArrowLeft size={14} />}
            variant="ghost"
            color="gray.600"
            fontWeight="normal"
            onClick={() => router.back()}
            {...goBackButtonProps}
          >
            Go Back
          </Button>
        </GridItem>

        <GridItem colSpan={{ base: 1, md: 8 }} {...gridItemProps}>
          <Box
            bg="white"
            borderRadius="xl"
            p="2rem"
            shadow="sm"
            border="1px solid"
            borderColor="gray.100"
            {...foodOrderContainerProps}
          >
            <VStack align="stretch" spacing="2rem" {...foodVstackProps}>
              <Box>
                <Heading
                  size="sm"
                  mb="1rem"
                  color="gray.600"
                  {...boxHeaderProps}
                >
                  Ticket*
                </Heading>
                {initialTicketCode && initialTicketDetail ? (
                  <Alert
                    status="success"
                    borderRadius="md"
                    {...initialTicketAlertProps}
                  >
                    <AlertIcon />
                    <Text {...initialTicketAlertTextProps}>
                      <Text>Linked to Ticket: </Text>
                      <Text fontWeight={"bold"}>
                        {initialTicketDetail.movie} - (
                        {initialTicketDetail.seats}) (
                        {format(
                          initialTicketDetail.show_date_time,
                          "MMM dd, hh:mm a",
                        )}
                        )
                      </Text>
                    </Text>
                  </Alert>
                ) : (
                  <Flex
                    align="stretch"
                    gap={2}
                    flexDirection={"column"}
                    flexBasis={"20%"}
                    {...ticketCodeSelectionBoxWrapperProps}
                  >
                    <Flex
                      flexDirection={"column"}
                      gap={2}
                      {...ticketCodeSelectionBoxProps}
                    >
                      {!initialTicketCode && !selectedTicket?.ticket_code && (
                        <Flex
                          flexDirection={{
                            base: "column",
                            sm: "row",
                          }}
                          gap={2}
                        >
                          <Input
                            placeholder="Enter Offline Ticket Code"
                            value={manualTicketCode}
                            onChange={(e) => {
                              setTicketStatus("");
                              handleTicketCheck(e.target.value.toUpperCase());
                              setManualTicketCode(e.target.value.toUpperCase());
                            }}
                            maxWidth={"250px"}
                            {...inputFieldProps}
                          />
                          {manualTicketCode && ticketStatus ? (
                            <Alert
                              alignItems={"center"}
                              gap={1}
                              status={
                                ticketStatus === "invalid" ? "error" : "success"
                              }
                              py={{
                                base: 2,
                                sm: 0,
                              }}
                              {...manualTicketStatusBoxProps}
                            >
                              {ticketStatus === "invalid" ? (
                                <>
                                  <AlertIcon />
                                  <Text {...manualTicketStatusTextProps}>
                                    Ticket not found
                                  </Text>
                                </>
                              ) : (
                                <Flex
                                  alignItems={"center"}
                                  justifyContent={"space-between"}
                                  flex={1}
                                  gap={1}
                                >
                                  <Text {...manualTicketStatusTextProps}>
                                    {offlineTicketDetail?.ticket_code && (
                                      <Text fontSize="sm">
                                        {offlineTicketDetail?.movie} -{" "}
                                        {offlineTicketDetail?.seats} (
                                        {format(
                                          new Date(
                                            offlineTicketDetail?.show_date_time,
                                          ),
                                          "MMM dd, hh:mm a",
                                        )}
                                        )
                                      </Text>
                                    )}
                                  </Text>
                                  <Flex gap={2}>
                                    <FaCheckCircle
                                      size={"25px"}
                                      color="green"
                                      cursor="pointer"
                                      onClick={() => {
                                        setSelectedTicket(
                                          offlineTicketDetail as UserPurchaseHistoryDTO,
                                        );
                                      }}
                                    />
                                    <Text {...confirmActionTextProps}>
                                      Confirm
                                    </Text>
                                    <FaTimesCircle
                                      size={"25px"}
                                      color="red"
                                      cursor="pointer"
                                      onClick={() => {
                                        setManualTicketCode("");
                                        setTicketStatus("");
                                      }}
                                    />
                                  </Flex>
                                </Flex>
                              )}
                            </Alert>
                          ) : (
                            <></>
                          )}
                        </Flex>
                      )}
                      {!(manualTicketCode || selectedTicket?.ticket_code) && (
                        <Box position="relative" padding="2" width={"250px"}>
                          <Divider {...sectionDividerProps} />
                          <AbsoluteCenter
                            bg={orDividerTextBg ?? "white"}
                            px="4"
                            fontSize={"sm"}
                            {...orDividerTextProps}
                          >
                            OR
                          </AbsoluteCenter>
                        </Box>
                      )}
                      {!manualTicketCode && !selectedTicket?.ticket_code && (
                        <Button
                          onClick={onOpen}
                          variant="outline"
                          colorScheme="primary"
                          isDisabled={isHistoryLoading}
                          width={"250px"}
                          {...selectTicketButtonProps}
                        >
                          Select from List
                        </Button>
                      )}
                    </Flex>

                    {selectedTicket?.ticket_code && (
                      <Alert
                        status="info"
                        borderRadius="md"
                        {...selectedTicketBoxProps}
                      >
                        <AlertIcon />
                        <Box flex={1}>
                          <Text fontWeight="bold" {...selectedTicketTextProps}>
                            Selected Ticket: {selectedTicket?.ticket_code}
                          </Text>
                          <Text fontSize="md" {...selectedTicketTextProps}>
                            {selectedTicket?.movie} - {selectedTicket?.seats} (
                            {format(
                              new Date(selectedTicket?.show_date_time),
                              "MMM dd, hh:mm a",
                            )}
                            )
                          </Text>
                        </Box>
                        <CloseButton
                          alignSelf="flex-start"
                          position="relative"
                          right={-1}
                          top={-1}
                          onClick={() => {
                            setManualTicketCode("");
                            setSelectedTicket(undefined);
                          }}
                          {...closeButtonProps}
                        />
                      </Alert>
                    )}
                  </Flex>
                )}
              </Box>

              <TicketSelectionModal
                isOpen={isOpen}
                onClose={onClose}
                purchaseHistory={purchaseHistory}
                onSelect={(code) => {
                  setManualTicketCode("");
                  const ticketDetail = purchaseHistory?.data?.records?.find(
                    (item) => item.ticket_code === code,
                  );
                  setSelectedTicket(ticketDetail);
                }}
                selectValue={selectedTicket?.ticket_code || ""}
                {...props}
              />

              <Divider {...sectionDividerProps} />

              <Box>
                <Heading
                  size="sm"
                  mb="1rem"
                  color="gray.600"
                  {...boxHeaderProps}
                >
                  Order Items
                </Heading>
                <VStack
                  align="stretch"
                  spacing="0.5rem"
                  {...orderItemListBoxWrapperProps}
                >
                  {foodBreakdown.map((item, idx) => (
                    <Flex
                      key={idx}
                      justify="space-between"
                      align="center"
                      {...orderItemListBoxProps}
                    >
                      <Box>
                        <Text fontWeight="medium" {...orderItemLabelProps}>
                          {item.title}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          {...orderItemQuantityProps}
                        >
                          Qty: {item.quantity}
                        </Text>
                      </Box>
                      <Text fontWeight="semibold" {...orderItemAmountProps}>
                        Rs. {item.amount * item.quantity}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>

              <Divider {...sectionDividerProps} />

              <Box>
                <WhatsAppOptIn
                  whatsapp={whatsapp}
                  isDisabled={isCheckoutLoading}
                  {...whatsappOptInProps}
                  // Styled like the ticket code field above unless the theme
                  // styles this box itself (no site theme passes it).
                  containerProps={{
                    borderColor: sectionDividerProps?.borderColor,
                    ...whatsappOptInProps?.containerProps,
                  }}
                  inputProps={{
                    ...inputFieldProps,
                    ...whatsappOptInProps?.inputProps,
                  }}
                />
                <PaymentSelector
                  value={paymentMethod}
                  member={user.member}
                  data={foodPaymentMethodData?.data}
                  loyaltyConfig={config.loyalty_config}
                  amount={total}
                  disableLoyalty={true}
                  onSelected={(method) => {
                    setPaymentMethod(method.idx);
                  }}
                  {...props}
                />
              </Box>

              <Flex justify="flex-end">
                <Button
                  size="lg"
                  colorScheme="blue"
                  isLoading={isCheckoutLoading}
                  isDisabled={
                    !(selectedTicket?.ticket_code || initialTicketCode) ||
                    !paymentMethod
                  }
                  onClick={handleProceedToPay}
                  px="3rem"
                  {...proceedToPayButton}
                >
                  Proceed to Pay
                </Button>
              </Flex>
            </VStack>
          </Box>
        </GridItem>

        <GridItem colSpan={{ base: 1, md: 4 }}>
          <AmountSummary
            total={total}
            amountBreakdown={[]}
            foodBreakdown={foodBreakdown}
            ticketSubtotal={0}
            foodSubtotal={total}
            three_d_charge={0}
            payViaLoyalty={false}
            loyaltyConfig={null}
            availableLoyaltyPoints={
              user?.member?.is_blacklisted
                ? 0
                : user?.member?.available_points || 0
            }
            {...props}
          />
        </GridItem>
      </Grid>
    </Box>
  );
}

export default FoodCheckout;
